import { t } from "@lingui/core/macro"
import * as Sentry from "@sentry/remix"
import { type PropsWithChildren, useEffect, useState } from "react"
import { useRouteLoaderData } from "react-router"
import { typeid } from "typeid-js"
import { Typography } from "~/components/ui/typography"
import type { loader as privateLoader } from "~/routes/_private/route"
import { not } from "~/utils"
import { cn } from "~/utils/style"
import { useEnv } from "../contexts/envs-context"

const errorBlob = "/images/error-blob.png"

type ErrorPageProps = {
  status: number
  heading: string
  description: string
}

export function ErrorPage({
  status,
  heading,
  description,
  children,
}: PropsWithChildren<ErrorPageProps>) {
  const baseUrl = useEnv("PUBLIC_BASE_URL_HOST")
  const [shouldRedirect, setShouldRedirect] = useState(false)
  const privateLoaderData =
    useRouteLoaderData<typeof privateLoader>("routes/_private")

  const isStatus500 = status >= 500
  const isLocal = baseUrl === "0.0.0.0"
  const isStatus500AndIsNotLocal = isStatus500 && not(isLocal)

  useEffect(() => {
    if (isStatus500AndIsNotLocal) {
      Sentry.withScope(scope => {
        scope.setTag("module", "app/components/ui/error-state")
        scope.captureEvent({
          message: "Error 500 on app/components/ui/error-page",
          level: "error",
          extra: {
            error: {
              status,
              heading,
              description,
            },
            userId: privateLoaderData?.user.id,
          },
        })
      })

      setShouldRedirect(true)
    }
  }, [
    isStatus500AndIsNotLocal,
    privateLoaderData?.user.id,
    status,
    heading,
    description,
  ])

  useEffect(() => {
    if (shouldRedirect) {
      const url = new URL(window.location.href)
      const redirectUrl = new URL("/update", url.origin)

      // NOTE: cb = cache burst. Cada vez que cair aqui, um novo valor será
      // gerado para esse parâmetro para garantir que a a versão mais recente
      // da plataforma seja baixada pelo browser.
      redirectUrl.searchParams.set("cb", typeid("cb").toString())

      window.location.href = redirectUrl.toString()
    }
  }, [shouldRedirect])

  if (isStatus500AndIsNotLocal) {
    return null
  }

  return (
    <div className="flex flex-1 items-center justify-center">
      <div className="flex flex-col gap-8 items-center">
        <div className="relative w-[500px] h-[448px]">
          <img
            src={errorBlob}
            alt={t({
              message: "Decoration image for a blob",
              comment: "ALT text for the blob that is shown in error pages",
            })}
          />
          <div
            className={cn(
              "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20",
              "flex flex-col gap-4 items-center",
            )}
          >
            <Typography fontWeight="bold" className="text-9xl">
              {status}
            </Typography>
            <div className="flex flex-col items-center gap-2 min-w-[364px]">
              <Typography
                variant="h1"
                as="h2"
                fontWeight="bold"
                textAlign="center"
              >
                {heading}
              </Typography>
              <Typography variant="muted" textAlign="center">
                {description}
              </Typography>
            </div>
          </div>
        </div>
        {children}
      </div>
    </div>
  )
}
